import createDOMPurify from "dompurify";
import { FontFamilies, FontSizes } from "../../config/formEnums";

/**
 * What the email's text may carry: emphasis, links, colour, size and font, a merge attribute,
 * and the blocks - paragraphs, headings, quotes and lists - with their alignment and indent.
 */
export const EmailHtmlTags = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "span",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "ul",
  "ol",
  "li",
];

export const MergeAttributeClass = "crmattribute";

const MergeAttributeData = [
  "data-g4-attribute",
  "data-g4-entity",
  "data-g4-defaultvalue",
  "data-g4-formatstring",
];

// Web, email and phone links, the portal's own `actions/` links, and in-page anchors.
const AllowedHref = /^(?:(?:https?|mailto|tel):|#|\/(?!\/)|\.{1,2}\/|actions\/)/i;

const Colour = /^(?:#[0-9a-f]{3}|#[0-9a-f]{6}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\))$/i;

// The browser may write either quote, so families are matched with one kind.
const familyKey = (value: string) => value.replace(/["']/g, "'").replace(/\s+/g, " ").trim().toLowerCase();

const Families = new Map<string, string>(Object.values(FontFamilies).map((family) => [familyKey(family), family]));
const Sizes = new Set<string>(Object.values(FontSizes).map((size) => size.toLowerCase()));

/** The font family from the list that a style value is, or empty when it is none of them. */
export const canonicalFamily = (value: string) => Families.get(familyKey(value)) ?? "";

const AllowedStyles: Record<string, RegExp> = {
  color: Colour,
  "background-color": Colour,
  "text-decoration": /^(?:underline|line-through|underline line-through|line-through underline)$/i,
  "font-weight": /^(?:bold|700|normal)$/i,
  "font-style": /^italic$/i,
  "text-align": /^(?:left|center|right|justify)$/i,
  "padding-left": /^\d{1,3}px$/i,
  "list-style-type": /^none$/i,
};

/** The value to keep for a style, or null when it is not one the editor offers. */
function allowedStyle(property: string, value: string) {
  if (property === "font-size") {
    return Sizes.has(value.toLowerCase()) ? value.toLowerCase() : null;
  }

  if (property === "font-family") {
    return Families.get(familyKey(value)) ?? null;
  }

  return AllowedStyles[property]?.test(value) ? value : null;
}

function filterStyle(style: string) {
  return style
    .split(";")
    .map((declaration) => {
      const colon = declaration.indexOf(":");

      return colon < 0
        ? null
        : [declaration.slice(0, colon).trim().toLowerCase(), declaration.slice(colon + 1).trim()];
    })
    .map((pair) => {
      const value = pair && allowedStyle(pair[0], pair[1]);

      return pair && value ? `${pair[0]}: ${value}` : null;
    })
    .filter(Boolean)
    .join("; ");
}

const purify = createDOMPurify(window);

purify.addHook("uponSanitizeAttribute", (node, data) => {
  const name = data.attrName;

  if (name === "style") {
    const style = filterStyle(data.attrValue);

    data.attrValue = style;
    data.keepAttr = style !== "";
  } else if (name === "class") {
    data.keepAttr = node.nodeName === "SPAN" && data.attrValue === MergeAttributeClass;
  } else if (name === "target") {
    data.keepAttr = data.attrValue === "_blank";
  } else if (name === "href") {
    data.keepAttr = AllowedHref.test(data.attrValue.trim());
  } else if (MergeAttributeData.includes(name)) {
    data.keepAttr = node.nodeName === "SPAN" && node.getAttribute("class") === MergeAttributeClass;
  }
});

/**
 * The email text with anything outside what the editor offers taken out, so what is saved
 * can be shown to every reader of the email and cannot change the layout around it.
 */
export function sanitiseEmailHtml(html: string | null | undefined): string {
  if (!html) {
    return "";
  }

  return purify.sanitize(html, {
    ALLOWED_TAGS: EmailHtmlTags,
    ALLOWED_ATTR: ["href", "target", "style", "class", ...MergeAttributeData],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    // Non-URL attributes are otherwise tested against the link pattern as well.
    ADD_URI_SAFE_ATTR: ["target", ...MergeAttributeData],
    ALLOWED_URI_REGEXP: AllowedHref,
  });
}
